import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { EMPTY, catchError, firstValueFrom } from "rxjs";
import { MediaModel, DefaultAllowedExtensions, AnamneseUploadType, AnamneseFunctionALUploadType } from "../models/media.model";
import { FilePreviewDialogComponent } from "../components/file-preview-dialog/file-preview-dialog.component";
import { MediaService } from "./media.lib.service";
import { NotificationService } from "libs/tm-common/src/lib/services/notification.lib.service";

export type FilePreviewData = {
	orgFilename: string;
	fileType: string;
	blob: Blob;
	uploadType?: AnamneseFunctionALUploadType | AnamneseUploadType;
};

@Injectable({
	providedIn: "root"
})
export class FilePreviewService {
	private readonly mediaService = inject(MediaService);
	private readonly dialog = inject(MatDialog);
	private readonly notificationService = inject(NotificationService);

	/**
	 * Checks if a file type is eligible for preview
	 * @param filetype The file extension to check
	 * @returns boolean indicating if the file type can be previewed
	 */
	isFilePreviewAvailable(filetype: string): boolean {
		return Object.values(DefaultAllowedExtensions).includes(
			filetype?.toLowerCase() as DefaultAllowedExtensions
		);
	}

	/**
	 * Opens a file preview dialog for the given media file
	 * @param file The media file to preview
	 * @returns void
	 */
	openFilePreview(file: MediaModel): void {
		if (!file?._id || !file?.filetype || !this.isFilePreviewAvailable(file.filetype)) {
			return;
		}

		const promise: Promise<FilePreviewData> = this.downloadMediaFile(file);

		promise.then((data: FilePreviewData) => {
			this.openDialog(data);
		}).catch((_error) => {
			this.notificationService.showErrorMessage("fileNotFound", null, 10000);
		});
	}

	openFilePreviewMultiple3DModels(files: MediaModel[]): void {
		if (files.length === 0) {
			return;
		}

		// filter only 3D model files
		const modelFiles = files.filter((file) =>
			[DefaultAllowedExtensions.STL, DefaultAllowedExtensions.PLY].includes(
				file.filetype?.toLowerCase() as DefaultAllowedExtensions
			)
		);
		if (modelFiles.length === 0) {
			return;
		}

		const downloadPromises = modelFiles.map(file => this.downloadMediaFile(file));

		// wait for all downloads to complete
		Promise.all(downloadPromises).then((previewData: FilePreviewData[]) => {
			this.openDialog(previewData);
		}).catch((_error) => {
			this.notificationService.showErrorMessage("fileNotFound", null, 10000);
		});
	}

	private openDialog(data: FilePreviewData | FilePreviewData[]): void {
		this.dialog.open(FilePreviewDialogComponent, {
			width: "80vw",
			maxWidth: "80vw",
			maxHeight: "95vh",
			data: data,
			autoFocus: 'dialog'
		});
	}

	private downloadMediaFile(file: MediaModel): Promise<FilePreviewData> {
		return firstValueFrom(
			this.mediaService.getFileAsBlob(file._id).pipe(
				catchError((_error: any) => {
					return EMPTY;
				})
			)
		).then((blob: Blob) => ({
			orgFilename: file.orgFilename,
			fileType: file.filetype,
			blob: blob,
			uploadType: [DefaultAllowedExtensions.STL, DefaultAllowedExtensions.PLY].includes(
				file.filetype?.toLowerCase() as DefaultAllowedExtensions
			)
				? (file.uploadType as AnamneseFunctionALUploadType | AnamneseUploadType)
				: undefined,
		}));
	}
}
